// noinspection JSValidateTypes
import { Box, Button, Checkbox, FileUploader, Grid, GridItem, H2, H3, H4, InlineMessage, Input, Modal, Radio, Search,
    Select, StatefulTree, Table, Text, Textarea, Tooltip } from "@bigcommerce/big-design";
import {useEffect, useState} from "react";
import {Box as MuiBox, Divider, Icon, List, ListItemButton, ListItemText} from "@mui/material";
import { addAttributeValue, addProduct, addProductAttribute, getProductAttributes, refreshProductMapping,
    removeAllProductAttributes, removeAttributeValue, removeProduct, removeProductAttribute,
    searchAddedProductsFromIDs } from "../api/products";
import {addCombination, getCombinations, removeAllCombinations} from "../api/combinations";
import {showErrorMessage, showMessage} from "../utils/AlertMessage";
import {getAllBrands} from "../api/storefront/brands";
import {getAllCategories} from "../api/storefront/categories";
import { createProduct, createProductImages, createProductImagesFromURL, getInventoryBySKU, getProductByID,
         getProductDetailsByID, getProductThumbnailImage, searchPrimaryProducts, getAllProductOptions,
         createProductVariant } from "../api/storefront/products";
import {getAllProductDataBySKUs, redirectURLsToPrimaryProduct, setPrimaryProductPrice} from "../utils/Products";
import {sortAlphaNumeric} from "../utils/Math";
import {deleteProductURLRedirects} from "../utils/Storefront";

const ConfigurePrimaryProductModal = (props) => {
    const EMPTY = '';
    const PRODUCT_TYPE_PHYSICAL = 'physical';
    const PRODUCT_TYPE_DIGITAL = 'digital';

    const [isOpenNewProductDialog, setIsOpenNewProductDialog] = useState(false);
    const [newProductName, setNewProductName] = useState(EMPTY);
    const [newProductType, setNewProductType] = useState(PRODUCT_TYPE_PHYSICAL);
    const [productBrands, setProductBrands] = useState([]);
    const [newProductBrand, setNewProductBrand] = useState(-1);
    const [productCategories, setProductCategories] = useState([]);
    const [defaultSelectedCategories, setDefaultSelectedCategories] = useState([]);
    const [defaultExpandedCategories, setDefaultExpandedCategories] = useState([]);
    const [newProductSelectedCategories, setNewProductSelectedCategories] = useState([]);
    const [newProductDescription, setNewProductDescription] = useState(EMPTY);
    const [checkUploadImageLater, setCheckUploadImageLater] = useState(false);
    const [productImageFiles, setProductImageFiles] = useState([]);
    const [checkUseImageURL, setCheckUseImageURL] = useState(false);
    const [newProductImageURL, setNewProductImageURL] = useState(EMPTY);
    const [newProductSearchKeywords, setNewProductSearchKeywords] = useState(EMPTY);
    const [newProductCustomFields, setNewProductCustomFields] = useState([{name: EMPTY, value: EMPTY}]);
    const [newProductPageTitle, setNewProductPageTitle] = useState(EMPTY);
    const [newProductMetaKeywords, setNewProductMetaKeywords] = useState(EMPTY);
    const [newProductMetaDescription, setNewProductMetaDescription] = useState(EMPTY);

    const openNewProductDialog = async () => {
        props.setLoading(true);
        try {
            // Reset all fields
            setNewProductName(EMPTY);
            setNewProductType(PRODUCT_TYPE_PHYSICAL);
            const brands = await getAllBrands();
            brands.forEach(brand => {
                brand.value = brand.id;
                brand.content = brand.name;
                delete brand.id;
                delete brand.name;
            });
            setProductBrands(brands);
            setNewProductBrand(-1);
            const categories = await getAllCategories();
            setProductCategories(categories);
            setDefaultSelectedCategories([]);
            setDefaultExpandedCategories([]);
            setNewProductSelectedCategories([]);
            setNewProductDescription(EMPTY);
            setCheckUploadImageLater(false);
            setProductImageFiles([]);
            setCheckUseImageURL(false);
            setNewProductImageURL(EMPTY);
            setNewProductSearchKeywords(EMPTY);
            setNewProductCustomFields([{name: EMPTY, value: EMPTY}]);
            setNewProductPageTitle(EMPTY);
            setNewProductMetaKeywords(EMPTY);
            setNewProductMetaDescription(EMPTY);
        } finally {
            props.setLoading(false);
        }

        setIsOpenNewProductDialog(true);
        props.setIsOpen(false);
    }
    const handleNewProductNameChange = (event) => {
        setNewProductName(event.target.value);
    }
    const handleNewProductBrandChange = (value) => {
        setNewProductBrand(value);
    }
    const handleNewProductDescriptionChange = (event) => {
        setNewProductDescription(event.target.value);
    }
    const handleUploadImageLaterChange = () => {
        setProductImageFiles([]);
        if (!checkUploadImageLater === true)
            if (checkUseImageURL)
                handleUseImageURLChange();
        setCheckUploadImageLater(!checkUploadImageLater);
    }
    const handleNewProductImageFilesChange = (imageFiles) => {
        /* NOT NEEDED */
        /*if (imageFiles.length > 1) {
            showErrorMessage('File Upload Error', 'Cannot accept more than one file at product creation. You can upload more images later from the BigCommerce Storefront.');
            imageFiles = [imageFiles.at(0)];
        }*/
        setProductImageFiles(imageFiles);
    }
    const handleUseImageURLChange = () => {
        if (!checkUseImageURL === true) {
            if (checkUploadImageLater)
                handleUploadImageLaterChange();
            setProductImageFiles([]);
        } else
            setNewProductImageURL(EMPTY);
        setCheckUseImageURL(!checkUseImageURL);
    }
    const handleNewProductImageURLChange = (event) => {
        // Adjust the checkbox value based on the URL input
        if (checkUseImageURL !== (event.target.value.toString().trim().length > 0))
            handleUseImageURLChange();
        setNewProductImageURL(event.target.value);
    }
    const handleCategorySelection = (selection) => {
        setNewProductSelectedCategories(selection);
    }
    const resetCategorySelection = () => {
        setDefaultSelectedCategories([]);
        setDefaultExpandedCategories([]);
    }
    const handleNewProductSearchKeywordsChange = (event) => {
        setNewProductSearchKeywords(event.target.value);
    }
    const insertNewCustomField = () => {
        const tempCustomFields = [...newProductCustomFields];
        tempCustomFields.push({name: EMPTY, value: EMPTY});
        setNewProductCustomFields(tempCustomFields);
    }
    const deleteCustomField = (index) => {
        const tempCustomFields = [...newProductCustomFields];
        tempCustomFields.splice(index, 1);
        if (tempCustomFields.length === 0)
            tempCustomFields.push({name: EMPTY, value: EMPTY});
        setNewProductCustomFields(tempCustomFields);
    }
    const handleCustomFieldNameChange = (name, index) => {
        const tempCustomFields = [...newProductCustomFields];
        tempCustomFields.at(index).name = name;
        setNewProductCustomFields(tempCustomFields);
    }
    const handleCustomFieldValueChange = (value, index) => {
        const tempCustomFields = [...newProductCustomFields];
        tempCustomFields.at(index).value = value;
        setNewProductCustomFields(tempCustomFields);
    }
    const handleNewProductPageTitleChange = (event) => {
        setNewProductPageTitle(event.target.value);
    }
    const handleNewProductMetaKeywordsChange = (event) => {
        setNewProductMetaKeywords(event.target.value);
    }
    const handleNewProductMetaDescriptionChange = (event) => {
        setNewProductMetaDescription(event.target.value);
    }

    function isValidHttpUrl(urlString) {
        let url;
        try {
            url = new URL(urlString);
        } catch (_) {
            return false;
        }
        return url.protocol === "http:" || url.protocol === "https:";
    }

    const addNewPrimaryProduct = async () => {
        if (!newProductName || newProductName.trim().length === 0) {
            showErrorMessage('Required Field', 'Product Name is Required.');
        } else if (!newProductType || newProductType.trim().length === 0) {
            showErrorMessage('Required Field', 'Product Type is Required.');
        } else if (!checkUploadImageLater && !checkUseImageURL && productImageFiles.length === 0) {
            showErrorMessage('Required Field', 'Product Image is Required. Choose "Not now" if you wish to assign the image from the first variation you are about to link.');
        } else if (!checkUploadImageLater && checkUseImageURL && !isValidHttpUrl(newProductImageURL)) {
            showErrorMessage('Invalid URL', 'The URL you provided for the Product Image is not a valid URL. Please recheck the URL and try again.');
        } else {
            props.setLoading(true);
            try {
                // Add new primary product to store
                let result = await createProduct(newProductName,
                    newProductType,
                    newProductBrand,
                    newProductSelectedCategories,
                    newProductDescription,
                    newProductCustomFields,
                    /* Storefront Details */
                    newProductSearchKeywords,
                    /* SEO */
                    newProductPageTitle,
                    newProductMetaKeywords,
                    newProductMetaDescription);

                if (result.error) {
                    showErrorMessage('Error', result.message);
                } else {
                    const newProductID = result.data.id;

                    if (!checkUploadImageLater) {
                        if (!checkUseImageURL && productImageFiles.length > 0) {
                            // Add images to the created product
                            if (!await createProductImages(newProductID, productImageFiles)) {
                                showErrorMessage('Image Upload Error', 'Error adding images to the created product. Please add images manually from your BigCommerce Products menu.');
                            }
                        } else if (checkUseImageURL && newProductImageURL.trim().length > 0) {
                            // Add image to the created product
                            if (!await createProductImagesFromURL(newProductID, newProductImageURL.trim())) {
                                showErrorMessage('Image Upload Error', 'Error adding the image to the created product. Please add images manually from your BigCommerce Products menu.');
                            }
                        }
                    }

                    // Add new primary product to database
                    result = await addProduct(props.clientID, newProductID, newProductName);
                    if (result.error)
                        showErrorMessage('Error', result.message);
                    else {
                        showMessage('Created', result.message);
                        // Get image url using product id from store
                        const productResult = await getProductDetailsByID(newProductID);
                        /*if (!productResult.success) {
                            showErrorMessage('Error', 'Unable to read product details from your BigCommerce store.');
                        }*/
                        const productDetails = productResult.product;
                        // Add to UI
                        const newProductItem = {
                            id: newProductID,
                            name: newProductName,
                            categories: newProductSelectedCategories,
                            variations: 0,
                            stock: 0,
                            thumbnail_image_url: productDetails ? productDetails.thumbnail_image_url : EMPTY,
                            tiny_image_url: productDetails ? productDetails.tiny_image_url : EMPTY
                        };
                        //props.items.push(newProductItem);
                        props.setItems([newProductItem, ...props.items]);
                        setSelectedItems([newProductItem]);
                        // Reset item selection dependants
                        setAttributes([]);
                        setProductAttributes([]);
                        setSelectedAttributeIndex(-1);
                        setProductAttributeValues([]);
                        setSelectedAttributeValueIndex(-1);
                    }

                    // Task: Update public product mapping
                    // COMMENTING: No need to update as it is covered by Adding a New Attribute
                    //await refreshProductMapping(props.clientID);

                    setIsOpenNewProductDialog(false);
                    props.setIsOpen(true);
                }
            } finally {
                props.setLoading(false);
            }
        }
    };

    const [isOpenExistingProductDialog, setIsOpenExistingProductDialog] = useState(false);
    const [existingProductName, setExistingProductName] = useState(EMPTY);
    const [existingProducts, setExistingProducts] = useState([]);
    const [selectedExistingProducts, setSelectedExistingProducts] = useState([]);
    const [currentPageExistingProduct, setCurrentPageExistingProduct] = useState(1);
    const [itemsPerPageExistingProduct, setItemsPerPageExistingProduct] = useState(5);
    const [totalItemsExistingProduct, setTotalItemsExistingProduct] = useState(0);

    const loadPrimaryProducts = async (searchName, itemsPerPage, currentPage) => {

        const prmProducts = await searchPrimaryProducts(searchName.toString().trim(), itemsPerPage, currentPage);
        // Identify already added primary products to mark as disabled
        if (prmProducts.data.length > 0) {
            const productIDs = [];
            prmProducts.data.forEach((product) => productIDs.push(product.id));
            let addedProducts = await searchAddedProductsFromIDs(props.clientID, productIDs);
            // Mark as disabled
            if (addedProducts.length > 0)
                prmProducts.data.forEach((product) => {
                    product.disabled = addedProducts.map(p => p.id).indexOf(product.id) >= 0;
                });
        }
        // Set product data
        setExistingProducts(prmProducts.data);
        setTotalItemsExistingProduct(prmProducts.totalItems);

        return prmProducts.data;
    }
    const openExistingProductDialog = async () => {
        // Reset all fields
        setExistingProductName(EMPTY);
        setSelectedExistingProducts([]);
        setItemsPerPageExistingProduct(5);
        setCurrentPageExistingProduct(1);
        // Load primary products
        props.setLoading(true);
        loadPrimaryProducts(EMPTY, itemsPerPageExistingProduct, 1).finally(() => props.setLoading(false));

        setIsOpenExistingProductDialog(true);
        setIsOpenNewProductDialog(false);
    }
    const handleExistingProductNameChange = (event) => {
        if (event.target.value.toString().trim().length === 0) {
            // Load primary products back
            props.setLoading(true);
            loadPrimaryProducts(EMPTY, itemsPerPageExistingProduct, 1).finally(() => props.setLoading(false));
        }
        setExistingProductName(event.target.value);
    }
    const handleExistingProductSelection = (items) => {
        // Check allowed
        if (items.length > 0 && items[items.length - 1].disabled) {
            showErrorMessage('Already Added', 'The item you selected has already been added as a primary product.');
            return;
        }
        // Proceed
        if (items.length > 2)
            items.reverse();
        const currentSelection = items.pop();
        items = [];
        if (currentSelection)
            items.push(currentSelection);
        setSelectedExistingProducts(items);
    }
    const handleItemsPerPageExistingProductChange = (newRange) => {
        setItemsPerPageExistingProduct(newRange);
        setCurrentPageExistingProduct(1);
        // Load primary products
        props.setLoading(true);
        loadPrimaryProducts(existingProductName, newRange, 1).finally(() => props.setLoading(false));
    }
    const handleCurrentPageExistingProductChange = (newPage) => {
        setCurrentPageExistingProduct(newPage);
        // Load primary products
        props.setLoading(true);
        loadPrimaryProducts(existingProductName, itemsPerPageExistingProduct, newPage).finally(() => props.setLoading(false));
    }
    const onSubmitExistingProductSearch = async () => {
        setCurrentPageExistingProduct(1);
        // Load primary products
        props.setLoading(true);
        return await loadPrimaryProducts(existingProductName, itemsPerPageExistingProduct, 1).finally(() => props.setLoading(false));
    }
    const addExistingPrimaryProduct = async () => {
        if (selectedExistingProducts.length > 0) {
            const productID = selectedExistingProducts[0].id;
            const productName = selectedExistingProducts[0].name;
            const productCategories = selectedExistingProducts[0].categories;

            try {
                props.setLoading(true);
                // Add existing primary product to database
                const result = addProduct(props.clientID, productID, productName);
                if (result.error)
                    showErrorMessage('Error', result.message);
                else {
                    showMessage('Created', result.message);
                    // Get image url using product id from store
                    const productResult = await getProductDetailsByID(productID);
                    /*if (!productResult.success) {
                        showErrorMessage('Error', 'Unable to read product details from your BigCommerce store.');
                    }*/
                    const productDetails = productResult.product;
                    // Add to UI
                    const newProductItem = {
                        id: productID,
                        name: productName,
                        categories: productCategories,
                        variations: 0,
                        stock: 0,
                        thumbnail_image_url: productDetails ? productDetails.thumbnail_image_url : EMPTY,
                        tiny_image_url: productDetails ? productDetails.tiny_image_url : EMPTY
                    };
                    props.items.push(newProductItem);
                    setSelectedItems([newProductItem]);
                    // Reset item selection dependants
                    setAttributes([]);
                    setProductAttributes([]);
                    setSelectedAttributeIndex(-1);
                    setProductAttributeValues([]);
                    setSelectedAttributeValueIndex(-1);

                    setIsOpenExistingProductDialog(false);
                    setIsOpenNewProductDialog(false);
                    props.setIsOpen(true);
                }
            } finally {
                props.setLoading(false);
            }
        }
    }


    const [productName, setProductName] = useState(EMPTY);
    const handleProductNameChange = (event) => setProductName(event.target.value);

    const [currentItems, setCurrentItems] = useState([]);
    const [selectedItems, setSelectedItems] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(5);
    const [itemsDisabled, setItemsDisabled] = useState(false);
    const [imageViewerOpened, setImageViewerOpened] = useState(false);
    const [imageViewerSrc, setImageViewerSrc] = useState(EMPTY);
    const [imageViewerPosition, setImageViewerPosition] = useState({x: 0, y: 0});
    const [modalSize, setModalSize] = useState({width: 0, height: 0});

    const [attributes, setAttributes] = useState([]); // fetch from database
    const [productAttributes, setProductAttributes] = useState([]);
    const [productAttributeValues, setProductAttributeValues] = useState([]);

    useEffect(() => {
        const lazyLoadGridInfo = async () => {
            setItemsDisabled(true);
            const maxItems = currentPage * itemsPerPage;
            const lastItem = Math.min(maxItems, props.items.length);
            const firstItem = Math.max(0, maxItems - itemsPerPage);

            const slicedItems = props.items.slice(firstItem, lastItem);
            setCurrentItems(slicedItems); // Initially show items without image

            // LOAD PRODUCT DETAILS
            // Clone the array so we don't mutate state
            let updatedItems = await Promise.all(
                slicedItems.map(async (item) => {
                    try {
                        const result = await getProductByID(item.id);
                        let productDetails = result.success ? result.product : null;
                        let totalStock = 0;
                        if (item.combinations) {
                            const skuList = item.combinations.map(combination => combination.sku);
                            // Get total stock using list of SKUs from store (process in chunks to manage the load to storefront)
                            const chunkSize = 100;
                            let currentSliceStart = 0;
                            let currentSliceEnd = chunkSize;
                            while (currentSliceStart < skuList.length) {
                                if (currentSliceEnd > skuList.length)
                                    currentSliceEnd = skuList.length;
                                const currentSkuListSlice = skuList.slice(currentSliceStart, currentSliceEnd);

                                // Process data in chunks
                                totalStock += await getInventoryBySKU(currentSkuListSlice.join(','));

                                currentSliceStart = currentSliceEnd;
                                currentSliceEnd += chunkSize;
                            }
                        }
                        return {
                            ...item,
                            categories: productDetails ? productDetails.categories : [],
                            stock: totalStock
                        };
                    } catch (err) {
                        // Handle error (optional)
                        return {
                            ...item,
                            categories: [],
                            stock: 0
                        };
                    }
                })
            );
            setCurrentItems(updatedItems); // Update items with additional details

            // FETCH IMAGES
            // Clone the array so we don't mutate state
            updatedItems = await Promise.all(
                updatedItems.map(async (item) => {
                    try {
                        const images = await getProductThumbnailImage(item.id);
                        return {
                            ...item,
                            thumbnail_image_url: images.thumbnail_image_url || EMPTY,
                            tiny_image_url: images.tiny_image_url || EMPTY
                        };
                    } catch (err) {
                        // Handle error (optional)
                        return {
                            ...item,
                            thumbnail_image_url: EMPTY,
                            tiny_image_url: EMPTY
                        };
                    }
                })
            );
            setCurrentItems(updatedItems); // Update items with images
            setItemsDisabled(false);
        };

        lazyLoadGridInfo();
    }, [props.items, currentPage, itemsPerPage]);

    const onSubmitItemSearch = () => {
        setSelectedItems([]);
        props.setItems((prevItems) => {
            if (productName) {
                return prevItems.filter((item) => item.name.toLowerCase().includes(productName.toLowerCase()));
            }
            return props.primaryProductData;
        });
    };
    const handleItemSelection = async (items) => {
        if (items.length > 2)
            items.reverse();
        const currentSelection = items.pop();
        items = [];
        if (currentSelection)
            items.push(currentSelection);
        setSelectedItems(items);

        if (items.length > 0) {
            // Load attributes of the selected product from database
            const attributes = await getProductAttributes(props.clientID, currentSelection.id);
            setAttributes(attributes);
            loadProductAttributes(attributes);
        } else
            setProductAttributes([]);

        setSelectedAttributeIndex(-1);
        setProductAttributeValues([]);
        setSelectedAttributeValueIndex(-1);
    };
    const removePrimaryProduct = async () => {
        if (selectedItems.length > 0) {
            const result = await removeProduct(props.clientID, selectedItems[0].id);
            if (result.error)
                showErrorMessage('Error', result.message);
            else {
                showMessage('Removed', result.message);
                // Remove from UI
                const tempItems = [...props.items];
                tempItems.splice(props.items.indexOf(selectedItems[0]), 1);
                props.setItems(tempItems);
                props.primaryProductData.splice(props.primaryProductData.indexOf(selectedItems[0]), 1);
                setSelectedItems([]);
            }

            //Task 1: Delete all existing Redirects to the primary product
            await deleteProductURLRedirects(selectedItems[0].id);
            // Task 2: Update public product mapping
            await refreshProductMapping(props.clientID);
        }
    }
    const loadProductAttributes = (attributes) => {
        let tempArr = [];
        if (attributes)
            attributes.forEach((element) => {
                tempArr.push(element.name);
            });
        setProductAttributes(tempArr);
    }
    const loadProductAttributeValues = (attributeIndex) => {
        let tempArr = [];
        attributes.forEach((element, index) => {
            if (index === attributeIndex) {
                tempArr = [...element.values];
            }
        })
        setProductAttributeValues(tempArr);
    }
    const openImageViewer = (event, id) => {
        props.items.forEach((item, index) => {
            if (item.id === id) {
                setImageViewerSrc(item.thumbnail_image_url);
                setImageViewerPosition({
                    x: (modalSize.width + 20),
                    y: 50 + (index * 72) + event.clientY - event.target.y
                });
                setImageViewerOpened(true);
            }
        });
    }

    const [isOpenBulkImportDialog, setIsOpenBulkImportDialog] = useState(false);
    const [isOpenBulkImportConfirmationDialog, setIsOpenBulkImportConfirmationDialog] = useState(false);
    const [bulkImportTextContent, setBulkImportTextContent] = useState(EMPTY);

    const openBulkImportDialog = () => {
        setBulkImportTextContent(EMPTY);
        props.setIsOpen(false);
        setIsOpenBulkImportDialog(true);
    }
    const validateBulkProductData = (textContent) => {
        if (textContent.trim().length === 0) {
            showErrorMessage('Error', 'Please add product data to continue.');
            return false;
        }

        const lines = textContent.trim().split(/\r?\n/);

        for (let index = 0; index < lines.length; index++) {
            const line = lines[index];
            const columns = line.split('\t').filter(column => column.trim().length > 0);

            if (columns.length > 2) {
                showErrorMessage('Error', `Maximum number of columns accepted is 2. ${columns.length} columns found at line ${index + 1}.`);
                return false;
            }

            if (columns.length === 2 && columns[0].includes(' ')) {
                showErrorMessage('Error', `Invalid product SKU "${columns[0]}" at line ${index + 1}. Spaces are not allowed in SKUs.`);
                return false;
            }

            const combinations = columns[columns.length - 1].split(';').map(field => field.trim()).filter(field => field.length > 0);

            for (const combination of combinations) {
                if (!combination.includes('=')) {
                    showErrorMessage('Error', `Invalid Combination "${columns[columns.length - 1]}" at line ${index + 1}. Each combination must follow the format Attribute=Value.`);
                    return false;
                }
            }
        }

        return true;
    };
    const handleBulkImportTextContentChange = (event) => {
        setBulkImportTextContent(event.target.value);
    }
    const bulkImportProductData = async (textContent) => {
        try {
            if (textContent.toString().trim().length === 0) {
                showErrorMessage('Error', 'No input provided.');
                return;
            }

            props.setStatus(null, 'Validating variations');

            const textContentArr = textContent.toString().trim().split(/\r?\n/).filter(line => line.trim().length > 0);
            const cleanedText = textContentArr.join('\n');

            // Step 1: Validate format
            const isValid = validateBulkProductData(cleanedText);
            if (!isValid) return;

            let attributeValuesMap = {};

            props.setStatus(null, 'Detecting Attributes and Values');
            // Step 2: Collect all attribute values across all lines
            textContentArr.forEach((line, index) => {
                const columns = line.trim().split('\t').filter(col => col.trim().length > 0);
                const properties = columns[columns.length - 1].split(';').map(p => p.replaceAll('"', EMPTY).trim()).filter(p => p.length > 0);

                let lineAttributeNames = new Set();

                properties.forEach(property => {
                    const [attributeName, attributeValue] = property.split('=').map(p => p.trim());

                    // Validation: Check duplicate attribute names within a single line
                    if (lineAttributeNames.has(attributeName.toLowerCase())) {
                        showErrorMessage('Error', `Duplicate attribute name "${attributeName}" found in line ${index + 1}. Each attribute name must be unique per product.`);
                        throw new Error('Validation failed.');
                    }
                    lineAttributeNames.add(attributeName.toLowerCase());

                    if (!attributeValuesMap[attributeName]) attributeValuesMap[attributeName] = new Set();
                    attributeValuesMap[attributeName].add(attributeValue);
                });
            });

            // Step 3: Filter only varying attributes (more than one unique value)
            let varyingAttributes = Object.entries(attributeValuesMap)
                .filter(([_, values]) => values.size > 1)
                .sort(sortAlphaNumeric)
                .reduce((obj, [key, values]) => {
                    // Sort the attribute's values (numerically if possible, otherwise alphabetically)
                    obj[key] = Array.from(values).sort(sortAlphaNumeric);
                    return obj;
                }, {});

            if (Object.keys(varyingAttributes).length === 0) {
                showErrorMessage('Error', 'No varying attributes found. All attributes have fixed values.');
                return;
            }

            console.log('Varying attributes detected (sorted):', varyingAttributes);

            // Step 3.5: Validate that combinations are unique based on varying attributes only
            let combinationSignatures = new Set();
            textContentArr.forEach((line) => {
                const columns = line.trim().split('\t').filter(col => col.trim().length > 0);
                const properties = columns[columns.length - 1].split(';').map(p => p.replaceAll('"', '').trim());

                const combinationValues = [];
                for (const attributeName of Object.keys(varyingAttributes)) {
                    const matchedProperty = properties.find(property => {
                        const [attrName] = property.split('=').map(p => p.trim());
                        return attrName.toLowerCase() === attributeName.toLowerCase();
                    });
                    if (matchedProperty) {
                        const [, value] = matchedProperty.split('=').map(p => p.trim());
                        combinationValues.push(value);
                    } else {
                        combinationValues.push(''); // Empty value if not provided
                    }
                }

                const signature = combinationValues.join('||'); // Unique combination key
                if (combinationSignatures.has(signature)) {
                    showErrorMessage('Error', `Duplicate variations found. Please check the Custom Fields for missing attributes to differentiate the products.`);
                    throw new Error('Duplicate variation found.');
                }
                combinationSignatures.add(signature);
            });

            props.setStatus(null, 'Storing Attributes and Values');
            // Step 4: Insert varying attributes and their values to DB
            const totalAttributeValues = Object.keys(varyingAttributes).length + Object.values(varyingAttributes).reduce((sum, arr) => sum + arr.length, 0);
            let count = 0;
            for (const attributeName in varyingAttributes) {
                //const attributeExists = await checkAttributeExists(attributeName);
                //if (!attributeExists) {
                await addProductAttribute(props.clientID, selectedItems[0].id, attributeName);
                props.setStatus(null, `Storing Attributes and Values ${Math.round((++count/totalAttributeValues)*100)}%`);
                console.log('Added new attribute: ' + attributeName);
                //}

                for (const attributeValue of varyingAttributes[attributeName]) {
                    //const valueExists = await checkAttributeValueExists(attributeName, attributeValue);
                    //if (!valueExists) {
                    await addAttributeValue(props.clientID, selectedItems[0].id, attributeName, attributeValue);
                    props.setStatus(null, `Storing Attributes and Values ${Math.round((++count/totalAttributeValues)*100)}%`);
                    console.log('Added new value: ' + attributeValue);
                    //}
                }
            }

            // Step 5: Load attributes from DB
            const attributes = await getProductAttributes(props.clientID, selectedItems[0].id);
            if (!attributes || attributes.length === 0) {
                showErrorMessage('Error', 'No attributes found in the database. Please try again.');
                return;
            }

            setAttributes(attributes);
            loadProductAttributes(attributes);

            props.setStatus(null, 'Storing Variations');
            // Step 6: Insert combinations (Only varying attributes)
            const totalVariations = textContentArr.length;
            count = 0;
            // Check whether SKU is included
            const hasSKU = textContentArr.map(line => line.trim().split('\t').filter(col => col.trim().length > 0).length).filter(cols => cols < 2).length === 0;
            // If all lines has SKU column, sort all lines by the SKU
            const textContentArrSorted = hasSKU ? textContentArr.sort((a, b) => sortAlphaNumeric(a.split('\t')[0], b.split('\t')[0])) : textContentArr;
            const skuList = [];
            const variantProperties = [];
            for (const line of textContentArrSorted) {
                const columns = line.trim().split('\t').filter(col => col.trim().length > 0);
                // Consider only the lines with SKU column (there can be maximum 2 columns only)
                if (columns.length > 1) {
                    const sku = columns[0].trim();
                    skuList.push(sku); // Keep SKUs for later
                    // Find the properties of this variant
                    const properties = columns[columns.length - 1].split(';').map(p => p.replaceAll('"', '').trim());

                    // Build an array of the properties of the variant with each attribute and value
                    const valuesArray = [];
                    attributes.forEach(attribute => {
                        if (Object.keys(varyingAttributes).includes(attribute.name)) {
                            // Find matching property for the attribute
                            const matchedProperty = properties.find(property => {
                                const [attrName] = property.split('=').map(p => p.trim());
                                return attrName.toLowerCase() === attribute.name.toLowerCase();
                            });
                            // Add value of the matching property to the values array
                            if (matchedProperty) {
                                const [, value] = matchedProperty.split('=').map(p => p.trim());
                                valuesArray.push(value);
                            } else {
                                // Fallback: Put an empty value for the relevant attribute if the attribute is not in any of the properties
                                valuesArray.push('');
                            }
                        }
                    });

                    // Prepare the combination attributes array based on the picked value for each attribute
                    const combinationAttributesJSON = {};
                    valuesArray.forEach((value, index) => {
                        combinationAttributesJSON[`attr_${index + 1}`] = value;
                    });
                    combinationAttributesJSON.sku = sku;

                    console.log('New variation: ', combinationAttributesJSON);

                    const result = await addCombination(props.clientID, selectedItems[0].id, combinationAttributesJSON);
                    if (result?.error) {
                        showErrorMessage('Error', `Failed to add variation for SKU ${sku}: ${result.message}`);
                        return;
                    } else {
                        console.log('Added variation for SKU: ' + sku);
                        // Keep properties of each variant for later
                        variantProperties.push({
                            sku,
                            values: valuesArray
                        });
                    }

                    props.setStatus(null, `Storing Variations ${Math.round((++count/totalVariations)*100)}%`);
                }
            }

            props.setStatus(null, 'Adding Product Variants in Store');
            count = 0;
            if (variantProperties.length > 0) {
                // Get all product info to insert
                const products = await getAllProductDataBySKUs(skuList);
                // Get all product options and option values
                const productOptionsResult = await getAllProductOptions(selectedItems[0].id);
                if (!productOptionsResult.success) {
                    showErrorMessage('Error', `Unable to read product options of Product ID: ${selectedItems[0].id}`);
                    return;
                }
                const productOptions = productOptionsResult.options;
                // Add product variant in the store
                for (const variant of variantProperties) {
                    const sku = variant.sku;
                    const valuesArray = variant.values;
                    const optionValues = [];
                    for (const value of valuesArray) {
                        const index = valuesArray.indexOf(value);
                        const attributeName = attributes[index].name;
                        // Find IDs of product option and option value
                        //console.log(`attributeName: ${attributeName}, value: ${value}`);
                        const productOption = productOptions.find(option => option.display_name.toLowerCase() === attributeName.toLowerCase());
                        const optionID = productOption?.id;
                        const optionValueID = productOption.option_values.find(optionValue => optionValue.label.toLowerCase() === value.toLowerCase())?.id;
                        // Prepare option value object
                        optionValues.push({
                            option_id: optionID || 0,
                            option_display_name: attributeName,
                            id: optionValueID || 0,
                            label: value
                        });
                    }
                    // Get product info
                    const productInfo = products.find(product => product.sku === sku);
                    // Insert variant
                    const result = await createProductVariant(selectedItems[0].id, productInfo, optionValues);
                    if (!result) {
                        showErrorMessage('Error', `Failed to create a variant for SKU: ${sku}`);
                        return;
                    }
                    props.setStatus(null, `Adding Product Variants in Store ${Math.round((++count/totalVariations)*100)}%`);
                }
            }

            props.setStatus(null, 'Finalizing..');

            // Load combinations
            const combinations = await getCombinations(props.clientID, selectedItems[0].id);
            if (!combinations || combinations.length === 0) {
                showErrorMessage('Error', 'No variations found in the database. Please try again.');
                return;
            }
            selectedItems[0].combinations = combinations;

            if (skuList.length > 0) {
                // Task 1: Loop through SKUs and update primary product data
                props.setStatus(null, 'Adjusting Product Price');
                await setPrimaryProductPrice(selectedItems[0].id, skuList);
                // Task 2: Add redirection to the primary product
                props.setStatus(null, 'Adding Redirects to Primary');
                await redirectURLsToPrimaryProduct(skuList, selectedItems[0].id, attributes, combinations);
            }
            // Task 3: Update public product mapping
            props.setStatus(null, 'Updating Product Mapping');
            await refreshProductMapping(props.clientID, selectedItems[0].id);

            props.setStatus(null, 'Almost There..');
            setIsOpenBulkImportDialog(false);
            props.setIsOpen(true);

        } catch (error) {
            console.error('Error in bulk import:', error);
            //showErrorMessage('Error', `Bulk import failed: ${error.message}`);
        }
    };


    const [selectedAttributeIndex, setSelectedAttributeIndex] = useState(-1);
    const [selectedAttributeValueIndex, setSelectedAttributeValueIndex] = useState(-1);
    const [isOpenNewAttributeDialog, setIsOpenNewAttributeDialog] = useState(false);
    const [newAttributeName, setNewAttributeName] = useState(EMPTY);
    const [isOpenNewAttributeValueDialog, setIsOpenNewAttributeValueDialog] = useState(false);
    const [newAttributeValueName, setNewAttributeValueName] = useState(EMPTY);
    const handleAttributeClick = (event, index) => {
        setSelectedAttributeIndex(index);

        loadProductAttributeValues(index);
        setSelectedAttributeValueIndex(-1);
    };
    const handleAttributeValueClick = (event, index) => {
        setSelectedAttributeValueIndex(index);
    };
    const removeAttribute = async () => {
        const productAttributesTemp = JSON.parse(JSON.stringify(productAttributes));
        if (selectedAttributeIndex >= 0) {
            // Remove attribute in database
            const result = await removeProductAttribute(props.clientID, selectedItems[0].id, attributes[selectedAttributeIndex].name);
            if (result.error)
                showErrorMessage('Error', result.message);
            else {
                showMessage('Removed', result.message);
                // Remove from UI
                attributes.splice(selectedAttributeIndex, 1);
                productAttributesTemp.splice(selectedAttributeIndex, 1);
                setSelectedAttributeIndex(-1);
                setProductAttributes(productAttributesTemp);
                setProductAttributeValues([]);
                setSelectedAttributeValueIndex(-1);
            }

            // Task: Update public product mapping
            await refreshProductMapping(props.clientID, selectedItems[0].id);
        }
    }
    const remAttributeValue = async () => {
        const productAttributeValuesTemp = [...productAttributeValues];
        if (selectedAttributeValueIndex >= 0) {
            // Remove value of attribute in database
            const result = await removeAttributeValue(props.clientID, selectedItems[0].id, productAttributes[selectedAttributeIndex], productAttributeValues[selectedAttributeValueIndex]);
            if (result.error)
                showErrorMessage('Error', result.message);
            else {
                showMessage('Removed', result.message);
                // Remove from UI
                attributes.at(selectedAttributeIndex).values.splice(selectedAttributeValueIndex, 1);
                productAttributeValuesTemp.splice(selectedAttributeValueIndex, 1);
                setSelectedAttributeValueIndex(-1);
            }
        }
        setProductAttributeValues(productAttributeValuesTemp);
    }
    const openNewAttributeDialog = () => {
        setNewAttributeName(EMPTY);
        setIsOpenNewAttributeDialog(true);
    }
    const handleNewAttributeNameChange = (event) => {
        setNewAttributeName(event.target.value);
    }
    const addNewAttribute = async (attributeName) => {
        if (!attributeName || attributeName.trim().length === 0) {
            showErrorMessage('Required Field', 'Attribute Name is Required.');
        } else {
            // Get combinations of this product from database
            // Avoid this change if there are any combinations for this product
            const combinations = await getCombinations(props.clientID, selectedItems[0].id);
            if (combinations && combinations.length > 0) {
                showErrorMessage('Variations Exists', 'Unlink all existing variations of this product in order to add a new attribute.');
            } else {
                // Add new attribute in database
                const result = await addProductAttribute(props.clientID, selectedItems[0].id, attributeName);
                if (result.error)
                    showErrorMessage('Error', result.message);
                else {
                    showMessage('Added', result.message);
                    // Update the UI
                    attributes.push({name: attributeName, values: []});
                    productAttributes.push(attributeName);
                }

                // Task: Update public product mapping
                await refreshProductMapping(props.clientID, selectedItems[0].id);
            }
        }
        setIsOpenNewAttributeDialog(false);
    };
    const openNewAttributeValueDialog = () => {
        setNewAttributeValueName(EMPTY);
        setIsOpenNewAttributeValueDialog(true);
    }
    const handleNewAttributeValueNameChange = (event) => {
        setNewAttributeValueName(event.target.value);
    }
    const addNewAttributeValue = async (attributeValueName) => {
        if (!attributeValueName || attributeValueName.trim().length === 0) {
            showErrorMessage('Required Field', 'Value Name is Required.');
        } else {
            // Add new value for the attribute in database
            const result = await addAttributeValue(props.clientID, selectedItems[0].id, productAttributes[selectedAttributeIndex], attributeValueName.trim());
            if (result.error)
                showErrorMessage('Error', result.message);
            else {
                showMessage('Added', result.message);
                // Update the UI
                attributes.at(selectedAttributeIndex).values.push(attributeValueName);
                productAttributeValues.push(attributeValueName);
                setIsOpenNewAttributeValueDialog(false);
            }
        }
    };

    const closeModal = () => {
        setProductName(EMPTY);
        props.setItems(props.primaryProductData);
        setSelectedItems([]);
        setProductAttributes([]);
        setSelectedAttributeIndex(-1);
        setProductAttributeValues([]);
        setSelectedAttributeValueIndex(-1);
        props.setIsOpen(false);
    }

    return (
        <>
            <Modal
                actions={[
                    {
                        text: 'Close',
                        variant: 'secondary',
                        onClick: closeModal,
                    },
                    /*{text: 'Save', onClick: closeModal},*/
                ]}
                closeOnClickOutside={false}
                closeOnEscKey={true}
                isOpen={props.isOpen}
                onClose={closeModal}
            >
                <div ref={el => {
                    if (!el || modalSize.height === el.getBoundingClientRect().height) return;
                    setModalSize({width: el.getBoundingClientRect().width, height: el.getBoundingClientRect().height});
                }}
                     onMouseEnter={() => setImageViewerOpened(false)}
                     style={{minHeight: '60vh'}}>
                    <Grid gridColumns="1fr 1fr" paddingTop="30px">
                        <H2>Configure Primary Products</H2>
                        <div align="right">
                            <Button margin="xxSmall" actionType="normal" onClick={openNewProductDialog}>
                                + New Primary Product
                            </Button>
                        </div>
                    </Grid>
                    <Grid gridColumns="1fr 5fr" paddingTop="20px" style={{alignItems: 'baseline'}}>
                        <GridItem><Text bold={true}>Product Name</Text></GridItem>
                        <GridItem><Search onChange={handleProductNameChange} onSubmit={onSubmitItemSearch}
                                          value={productName}
                                          placeholder="Type a Primary Product Name to Search"/></GridItem>
                    </Grid>
                    <div className="wide-table" style={{ pointerEvents: itemsDisabled ? 'none' : 'auto', opacity: itemsDisabled ? 0.5 : 1 }}>
                        <Table
                            columns={[
                                {header: EMPTY, hash: 'id', render: () => {}, width: '0.01rem'},
                                {header: 'Name', hash: 'name', render: ({name}) => name},
                                {header: 'Variations', hash: 'variations', render: ({variations}) => variations, align: 'center'},
                                {header: 'Total Stock', hash: 'stock', render: ({stock}) => stock, align: 'center'},
                                {header: EMPTY, hash: 'tiny_image_url', render: ({tiny_image_url, id}) => {
                                        return (<MuiBox component='img' src={tiny_image_url}
                                                        onMouseEnter={(event) => openImageViewer(event, id)}
                                                        onMouseLeave={() => setImageViewerOpened(false)}/>)
                                    }, width: '5rem', align: 'center'},
                            ]}
                            items={currentItems}
                            selectable={{
                                selectedItems: selectedItems,
                                onSelectionChange: handleItemSelection,
                            }}
                            onMouseEnter={() => setImageViewerOpened(false)}
                            keyField="id"
                            pagination={{
                                currentPage: currentPage,
                                totalItems: props.items.length,
                                onPageChange: setCurrentPage,
                                itemsPerPageOptions: [5, 10],
                                itemsPerPage: itemsPerPage,
                                onItemsPerPageChange: (newRange) => {
                                    setCurrentPage(1);
                                    setItemsPerPage(newRange);
                                }
                            }}
                        />
                    </div>
                    <div className={!imageViewerOpened ? 'force-hide' : ''}
                         style={{
                             position: "absolute",
                             left: imageViewerPosition.x,
                             top: imageViewerPosition.y,
                             zIndex: 1100
                         }}>
                        <MuiBox component='img' src={imageViewerSrc} sx={{boxShadow: 3}}/>
                    </div>
                    <div className={props.items.length === 0 ? 'force-hide' : ''} align="right">
                        <Button marginVertical="small" actionType="destructive" onClick={removePrimaryProduct}
                                disabled={selectedItems.length === 0}>
                            - Remove Product
                        </Button>
                    </div>
                    <div className={(props.items.length > 0 || selectedItems.length > 0) ? 'force-hide' : ''}>
                        <InlineMessage
                            marginVertical="small"
                            messages={[{text: 'Make sure you created a primary product in order to configure the properties. Please select your preferred primary product from the above list.'}]}
                            type="warning"
                        />
                    </div>
                    <div className={selectedItems.length === 0 ? 'force-hide' : ''}>
                        <Button marginVertical="small" actionType="normal" variant="secondary"
                                onClick={openBulkImportDialog} iconLeft={<Icon>save_alt</Icon>}>
                            Bulk Import
                        </Button>
                        <Grid gridColumns="1fr 1fr">
                            <GridItem>
                                <center>
                                    <H4>Attributes</H4>
                                    <List component="nav" aria-label="secondary mailbox folders"
                                          style={{overflowY: "scroll", maxHeight: "40vh"}}>
                                        {productAttributes.map((label, index) => (
                                            <ListItemButton
                                                selected={selectedAttributeIndex === index}
                                                onClick={(event) => handleAttributeClick(event, index)}
                                            >
                                                <ListItemText primary={label}/>
                                            </ListItemButton>
                                        ))}
                                    </List>
                                </center>
                                <div align="right">
                                    <Box padding="small">
                                        <Button margin="xxSmall" actionType="normal" onClick={openNewAttributeDialog}>
                                            + Add New
                                        </Button>
                                        <Button margin="xxSmall" actionType="destructive" onClick={removeAttribute}
                                                disabled={selectedAttributeIndex < 0}>
                                            - Remove
                                        </Button>
                                    </Box>
                                </div>
                            </GridItem>
                            <GridItem className={selectedAttributeIndex < 0 ? 'force-hide' : ''}>
                                <center>
                                    <H4>Values</H4>
                                    <List component="nav" aria-label="secondary mailbox folders"
                                          style={{overflowY: "scroll", maxHeight: "40vh"}}>
                                        {productAttributeValues.map((label, index) => (
                                            <ListItemButton
                                                selected={selectedAttributeValueIndex === index}
                                                onClick={(event) => handleAttributeValueClick(event, index)}
                                            >
                                                <ListItemText primary={label}/>
                                            </ListItemButton>
                                        ))}
                                    </List>
                                </center>
                                <div align="right">
                                    <Box padding="small">
                                        <Button margin="xxSmall" actionType="normal"
                                                onClick={openNewAttributeValueDialog}>
                                            + Add New
                                        </Button>
                                        <Button margin="xxSmall" actionType="destructive" onClick={remAttributeValue}
                                                disabled={selectedAttributeValueIndex < 0}>
                                            - Remove
                                        </Button>
                                    </Box>
                                </div>
                            </GridItem>
                        </Grid>
                    </div>
                </div>
            </Modal>
            <Modal
                variant="dialog"
                actions={[
                    {
                        text: 'Cancel',
                        variant: 'subtle',
                        onClick: () => {
                            props.setIsOpen(true);
                            setIsOpenNewProductDialog(false);
                        },
                    },
                    {
                        text: '+ Add',
                        onClick: () => addNewPrimaryProduct(),
                        disabled: newProductName.trim().length === 0
                    },
                ]}
                closeOnClickOutside={false}
                closeOnEscKey={true}
                isOpen={isOpenNewProductDialog}
                onClose={() => {
                    props.setIsOpen(true);
                    setIsOpenNewProductDialog(false);
                }}
            >
                <div style={{maxHeight: '70vh'}}>
                    <Grid gridColumns="1fr 1fr" paddingTop="30px">
                        <H2>Create New Primary Product</H2>
                        <div align="right">
                            <Button margin="xxSmall" actionType="normal" variant="utility"
                                    onClick={openExistingProductDialog}>
                                Choose Existing Product
                            </Button>
                        </div>
                    </Grid>
                    <Grid gridColumns="1fr 5fr" paddingTop="10px">
                        <Text bold={true}>Product Name</Text>
                        <Input
                            onChange={handleNewProductNameChange}
                            placeholder="Type New Product Name (represents all variations)"
                            type="text"
                            required={true}
                            value={newProductName}
                        />
                        <Text bold={true}>Product Type</Text>
                        <Grid gridColumns="1fr 1fr 5fr">
                            <Radio checked={newProductType === PRODUCT_TYPE_PHYSICAL} label="Physical"
                                   onChange={(event) => {
                                       setNewProductType(event.target.value)
                                   }} value={PRODUCT_TYPE_PHYSICAL}/>
                            <Radio checked={newProductType === PRODUCT_TYPE_DIGITAL} label="Digital"
                                   onChange={(event) => {
                                       setNewProductType(event.target.value)
                                   }} value={PRODUCT_TYPE_DIGITAL}/>
                        </Grid>
                        <Text bold={true}>Brand</Text>
                        <Select
                            filterable={true}
                            onOptionChange={handleNewProductBrandChange}
                            options={productBrands}
                            placeholder="Choose brand"
                            value={newProductBrand}
                        />
                        <div style={{display: "flex"}}>
                            <Text bold={true}>Categories</Text>
                            <Tooltip placement="right"
                                     trigger={<Icon color="action" fontSize="small"
                                                    style={{padding: '3px', opacity: 0.5}}>info</Icon>}>
                                Category selection should be identical to the variations you are about to link.
                            </Tooltip>
                        </div>
                        <div>
                            <div style={{height: '200px', overflowY: "scroll", scrollbarWidth: "thin"}}>
                                <StatefulTree
                                    nodes={productCategories}
                                    defaultSelected={defaultSelectedCategories}
                                    defaultExpanded={defaultExpandedCategories}
                                    selectable="multi"
                                    onSelectionChange={handleCategorySelection}
                                />
                            </div>
                            <div style={{display: "flex", justifyContent: "right"}}>
                                <Button margin="xxSmall" variant="secondary" actionType="destructive"
                                        onClick={resetCategorySelection}
                                        disabled={newProductSelectedCategories.length === 0}>
                                    Reset Selection
                                </Button>
                            </div>
                        </div>
                        <Text bold={true}>Description</Text>
                        <Textarea
                            onChange={handleNewProductDescriptionChange}
                            placeholder="Type a Common Description"
                            resize={false}
                            rows={3}
                            value={newProductDescription}
                        />
                        <Text bold={true}>Upload Image</Text>
                        <GridItem>
                            <div style={{display: "flex"}}>
                                <Checkbox
                                    checked={checkUploadImageLater}
                                    label='Not now'
                                    onChange={handleUploadImageLaterChange}
                                />
                                <Tooltip placement="right"
                                         trigger={<Icon color="action" fontSize="small"
                                                        style={{padding: '3px', opacity: 0.5}}>info</Icon>}>
                                    Automatically pick the image from the first variation you are about to link.
                                </Tooltip>
                            </div>
                            <div style={{height: "10px"}}/>
                            <div className="file-uploader">
                                <FileUploader
                                    /*accept="image/bmp,image/jpeg,image/png,image/vnd.wap.wbmp,image/xbm,image/webp,image/svg+xml"*/
                                    accept="image/*"
                                    dropzoneConfig={{
                                        label: 'Drag & Drop product image here to upload.',
                                        description: 'bmp, gif, jpeg, png, wbmp, xbm or webp. Maximum 8 MB.',
                                    }}
                                    files={productImageFiles}
                                    onFilesChange={handleNewProductImageFilesChange}
                                    multiple
                                    required
                                    validators={[
                                        {
                                            validator: (file) => {
                                                const MB = 1024 * 1024;
                                                return file.size <= 8 * MB;
                                            },
                                            type: 'file-size',
                                            message: 'Please upload a file less than 8 MB.'
                                        },
                                        {
                                            validator: (file) => {
                                                return file.type.startsWith('image/')
                                            },
                                            type: 'file-format',
                                            message: 'Invalid file type "{FILE_NAME}". Please upload bmp, gif, jpeg, png, wbmp, xbm or webp.'
                                        },
                                    ]}
                                    onFilesError={(errors) => {
                                        errors.forEach((error) => {
                                            showErrorMessage('File Upload Error', error.message.replace('{FILE_NAME}', error.file.name));
                                            productImageFiles.splice(productImageFiles.indexOf(error.file), 1);
                                        });
                                    }}
                                    disabled={checkUploadImageLater || checkUseImageURL}
                                />
                            </div>
                            <div style={{height: "10px"}}/>
                            <Grid gridColumns="3fr 7fr">
                                <GridItem style={{display: "flex", alignItems: "center"}}>
                                    <Checkbox
                                        checked={checkUseImageURL}
                                        label='Use Image URL'
                                        onChange={handleUseImageURLChange}
                                        disabled={checkUploadImageLater}
                                    />
                                    <Tooltip placement="right"
                                             trigger={<Icon color="action" fontSize="small"
                                                            style={{padding: '3px', opacity: 0.5}}>info</Icon>}>
                                        Public URL of an image from your BigCommerce store or anywhere else on the
                                        web.
                                    </Tooltip>
                                </GridItem>
                                <Input
                                    onChange={handleNewProductImageURLChange}
                                    placeholder="Paste the image URL here ..."
                                    type="text"
                                    maxLength={255}
                                    value={newProductImageURL}
                                    disabled={checkUploadImageLater}
                                />
                            </Grid>
                            <div className={checkUploadImageLater ? 'force-hide' : ''}>
                                <InlineMessage
                                    marginVertical="small"
                                    messages={[{text: 'The first image to upload will become the product Thumbnail by default. You can change it later from the BigCommerce Storefront.'}]}
                                    type="info"
                                />
                            </div>
                        </GridItem>
                    </Grid>
                    <Divider style={{margin: '20px'}}/>
                    <center>
                        <H3>Storefront</H3>
                    </center>
                    <Grid gridColumns="2fr 7fr" paddingTop="20px">
                        <div style={{display: "flex"}}>
                            <Text bold={true}>Search Keywords</Text>
                            <Tooltip placement="right" trigger={<Icon color="action" fontSize="small" style={{
                                padding: '3px',
                                opacity: 0.5
                            }}>info</Icon>}>
                                The search keywords are optional, but if entered will be used to assist people when
                                searching your products using on-site search. Enter keywords separated by commas, such
                                as: widget, affordable, portable.
                            </Tooltip>
                        </div>
                        <Input
                            onChange={handleNewProductSearchKeywordsChange}
                            placeholder="e.g. Widget, affordable, portable, etc..."
                            type="text"
                            value={newProductSearchKeywords}
                        />
                        <Text bold={true}>Custom Fields</Text>
                        <GridItem>
                            <Grid gridColumns="3fr 6fr 1fr">
                                {newProductCustomFields.map((customField, index) => (
                                    <>
                                        <Input
                                            onChange={(event) => handleCustomFieldNameChange(event.target.value, index)}
                                            placeholder="Field Name"
                                            type="text"
                                            value={customField.name}
                                        />
                                        <Input
                                            onChange={(event) => handleCustomFieldValueChange(event.target.value, index)}
                                            placeholder="Field Value"
                                            type="text"
                                            value={customField.value}
                                        />
                                        <Button iconOnly={<Icon color="action" fontSize="small"
                                                                style={{opacity: 0.6}}>remove_circle</Icon>}
                                                variant="subtle" onClick={() => deleteCustomField(index)}/>
                                    </>
                                ))}
                            </Grid>
                            <Button variant="subtle" actionType="normal" marginVertical="small"
                                    onClick={insertNewCustomField}>
                                + Add Custom Field
                            </Button>
                        </GridItem>
                    </Grid>
                    <Divider style={{margin: '20px'}}/>
                    <center>
                        <H3>Search Engine Optimization</H3>
                    </center>
                    <Grid gridColumns="2fr 7fr" paddingTop="20px">
                        <div style={{display: "flex"}}>
                            <Text bold={true}>Page Title</Text>
                            <Tooltip placement="right" trigger={<Icon color="action" fontSize="small" style={{
                                padding: '3px',
                                opacity: 0.5
                            }}>info</Icon>}>
                                Specify a page title, or leave blank to use the product’s name as the page title.
                            </Tooltip>
                        </div>
                        <Input
                            onChange={handleNewProductPageTitleChange}
                            type="text"
                            value={newProductPageTitle}
                        />
                        <div style={{display: "flex"}}>
                            <Text bold={true}>Meta Keywords</Text>
                            <Tooltip placement="right" trigger={<Icon color="action" fontSize="small" style={{
                                padding: '3px',
                                opacity: 0.5
                            }}>info</Icon>}>
                                Specify the keywords that will appear in the relevant meta tag for this product.
                            </Tooltip>
                        </div>
                        <Input
                            onChange={handleNewProductMetaKeywordsChange}
                            type="text"
                            value={newProductMetaKeywords}
                        />
                        <div style={{display: "flex"}}>
                            <Text bold={true}>Meta Description</Text>
                            <Tooltip placement="right" trigger={<Icon color="action" fontSize="small" style={{
                                padding: '3px',
                                opacity: 0.5
                            }}>info</Icon>}>
                                Specify the description that will appear in the relevant meta tag for this product.
                            </Tooltip>
                        </div>
                        <Textarea
                            onChange={handleNewProductMetaDescriptionChange}
                            resize={false}
                            rows={3}
                            value={newProductMetaDescription}
                        />
                    </Grid>
                    <div style={{height: "10px"}}/>
                </div>
            </Modal>
            <Modal
                variant="dialog"
                actions={[
                    {
                        text: 'Cancel',
                        variant: 'subtle',
                        onClick: () => {
                            setIsOpenExistingProductDialog(false);
                            setIsOpenNewProductDialog(true);
                        },
                    },
                    {
                        text: '+ Add',
                        onClick: () => addExistingPrimaryProduct(),
                        disabled: selectedExistingProducts.length === 0
                    },
                ]}
                closeOnClickOutside={false}
                closeOnEscKey={true}
                header="Choose Existing Product"
                isOpen={isOpenExistingProductDialog}
                onClose={() => {
                    setIsOpenExistingProductDialog(false);
                    setIsOpenNewProductDialog(true);
                }}
            >
                <div className="equal-rows-table" style={{marginTop: '0.2rem'}}>
                    <Search onChange={handleExistingProductNameChange}
                            onSubmit={onSubmitExistingProductSearch} value={existingProductName}
                            placeholder="Search (e.g. Name, Description, etc.)"/>
                    <div className={existingProducts.length > 0 ? 'force-hide' : ''}>
                        <InlineMessage
                            marginVertical="small"
                            messages={[{text: 'Primary Products are not directly sold with an SKU. In case you cannot find the product, make sure it has an empty value for the SKU.'}]}
                            type="info"
                        />
                    </div>
                    <Table
                        columns={[
                            {
                                header: EMPTY,
                                hash: 'id',
                                render: () => EMPTY,
                                width: '0rem'
                            },
                            {
                                header: 'Name',
                                hash: 'name',
                                render: ({name, disabled}) => <Text
                                    color={disabled ? "danger50" : "currentColor"}>{name}</Text>
                            },
                            {
                                header: EMPTY, hash: 'tiny_image_url', render: ({tiny_image_url, id}) => {
                                    return (<MuiBox component='img' src={tiny_image_url}
                                                    onMouseEnter={(event) => openImageViewer(event, id)}
                                                    onMouseLeave={() => setImageViewerOpened(false)}/>)
                                },
                                width: '5rem'
                            },
                        ]}
                        items={existingProducts}
                        selectable={{
                            selectedItems: selectedExistingProducts,
                            onSelectionChange: handleExistingProductSelection
                        }}
                        onMouseEnter={() => setImageViewerOpened(false)}
                        keyField="sku"
                        pagination={{
                            currentPage: currentPageExistingProduct,
                            totalItems: totalItemsExistingProduct,
                            onPageChange: handleCurrentPageExistingProductChange,
                            itemsPerPageOptions: [5, 10],
                            itemsPerPage: itemsPerPageExistingProduct,
                            onItemsPerPageChange: handleItemsPerPageExistingProductChange
                        }}
                    />
                    <div className={!imageViewerOpened ? 'force-hide' : ''}
                         style={{
                             position: "absolute",
                             left: imageViewerPosition.x,
                             top: imageViewerPosition.y,
                             zIndex: 1100
                         }}>
                        <MuiBox component='img' src={imageViewerSrc} sx={{boxShadow: 3}}/>
                    </div>
                </div>
                <div className={existingProducts.length === 0 ? 'force-hide' : ''}>
                    <InlineMessage
                        marginVertical="xxSmall"
                        messages={[{text: 'Recently modified products displayed first.'}]}
                        type="success"
                    />
                </div>
            </Modal>
            <Modal
                actions={[
                    {
                        text: 'Cancel',
                        variant: 'subtle',
                        onClick: () => {
                            setIsOpenBulkImportDialog(false);
                            props.setIsOpen(true);
                        },
                    },
                    {
                        text: 'Import',
                        onClick: async () => {
                            // Check combinations exists for the selected product
                            const combinations = await getCombinations(props.clientID, selectedItems[0].id);
                            if (combinations.length > 0) {
                                setIsOpenBulkImportConfirmationDialog(true);
                            } else {
                                props.setLoading(true);
                                props.setStatus('Importing Variations');
                                bulkImportProductData(bulkImportTextContent).finally(() =>{
                                    props.setLoading(false);
                                    setIsOpenBulkImportDialog(false);
                                    props.setIsOpen(true);
                                });
                            }
                        },
                        disabled: !bulkImportTextContent
                    },
                ]}
                closeOnClickOutside={false}
                closeOnEscKey={true}
                header="Bulk Import Product Data"
                isOpen={isOpenBulkImportDialog}
                onClose={() => {
                    setIsOpenBulkImportDialog(false);
                    props.setIsOpen(true);
                }}
            >
                <H4>Paste all combinations from your spreadsheet data in the following format (multiline):</H4>
                <Text>Include product SKU at the beginning of each line to link the secondary product automatically
                    (optional). If you have the
                    attributes and values of your products in Custom Fields, you could bulk export products from your
                    store and use that spreadsheet to <b>Copy the "SKU" column and the "Custom Fields" column</b> of a
                    single product (many variations) and paste them
                    here.</Text>
                <Textarea
                    rows={7}
                    onChange={handleBulkImportTextContentChange}
                    value={bulkImportTextContent}
                    placeholder={'SKU01 "Attribute01=A1Value01";"Attribute02=A2Value"' +
                        '\nSKU02 "Attribute01=A1Value02";"Attribute02=A2Value"' +
                        '\n\nAbove two combinations are based on two attributes where Attribute01 has two Values and Attribute02 has only one Value. ' +
                        'These two combinations are linked to the products SKU01 and SKU02 respectively.'}
                    resize={false}
                    spellCheck={false}
                    error=""
                    required
                />
                <Text>You could verify and adjust the imported values later.</Text>
            </Modal>
            <Modal
                variant="dialog"
                actions={[
                    {
                        text: 'Cancel',
                        variant: 'subtle',
                        onClick: () => setIsOpenBulkImportConfirmationDialog(false),
                    },
                    {
                        text: 'Import & Replace', onClick: async () => {
                            setIsOpenBulkImportConfirmationDialog(false);
                            props.setLoading(true);
                            // Delete product variants
                            /*props.setStatus('Removing Product Variants in Store');
                            const variantsResult = await getAllProductVariants(selectedItems[0].id);
                            if (variantsResult.success) {
                                const variants = variantsResult.variants;
                                for (const variant of variants) {
                                    await deleteProductVariant(selectedItems[0].id, variant.id);
                                }
                            }*/
                            // Delete current Combinations, Attributes, and Values
                            props.setStatus('Removing Variations');
                            const result = await removeAllCombinations(props.clientID, selectedItems[0].id);
                            if (result.error) {
                                props.setLoading(false);
                                showErrorMessage('Error', result.message);
                            } else {
                                // Delete all existing Redirects to the primary product
                                props.setStatus('Removing Redirects');
                                await deleteProductURLRedirects(selectedItems[0].id);

                                props.setStatus('Removing Attributes and Values');
                                const result2 = await removeAllProductAttributes(props.clientID, selectedItems[0].id);
                                if (result2.error) {
                                    props.setLoading(false);
                                    showErrorMessage('Error', result2.message);
                                } else {
                                    props.setStatus('Importing Variations');
                                    bulkImportProductData(bulkImportTextContent).finally(() => {
                                        props.setLoading(false);
                                        setIsOpenBulkImportDialog(false);
                                        props.setIsOpen(true);
                                    });
                                }
                            }
                        }
                    },
                ]}
                closeOnClickOutside={false}
                closeOnEscKey={true}
                header="Import Product Data"
                isOpen={isOpenBulkImportConfirmationDialog}
                onClose={() => setIsOpenBulkImportConfirmationDialog(false)}
            >
                <Text>Are you sure you want to replace existing variations with these entries? Any linked products will be unlinked from the primary product first.</Text>
            </Modal>
            <Modal
                variant="dialog"
                actions={[
                    {
                        text: 'Cancel',
                        variant: 'subtle',
                        onClick: () => setIsOpenNewAttributeDialog(false),
                    },
                    {text: '+ Add', onClick: () => addNewAttribute(newAttributeName), disabled: !newAttributeName},
                ]}
                closeOnClickOutside={false}
                closeOnEscKey={true}
                header="Add New Attribute"
                isOpen={isOpenNewAttributeDialog}
                onClose={() => setIsOpenNewAttributeDialog(false)}
            >
                <Input
                    label="Name"
                    onChange={handleNewAttributeNameChange}
                    placeholder="Type Attribute Name here ..."
                    type="text"
                    required={true}
                    value={newAttributeName}
                />
            </Modal>
            <Modal
                variant="dialog"
                actions={[
                    {
                        text: 'Cancel',
                        variant: 'subtle',
                        onClick: () => setIsOpenNewAttributeValueDialog(false),
                    },
                    {
                        text: '+ Add',
                        onClick: () => addNewAttributeValue(newAttributeValueName),
                        disabled: !newAttributeValueName
                    },
                ]}
                closeOnClickOutside={false}
                closeOnEscKey={true}
                header={"Add New Value for '" + productAttributes.at(selectedAttributeIndex) + "'"}
                isOpen={isOpenNewAttributeValueDialog}
                onClose={() => setIsOpenNewAttributeValueDialog(false)}
            >
                <Input
                    label="Name"
                    onChange={handleNewAttributeValueNameChange}
                    placeholder="Type Value Name here ..."
                    type="text"
                    required={true}
                    value={newAttributeValueName}
                />
            </Modal>
        </>
    );
}

export default ConfigurePrimaryProductModal;